import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import imageUploadService from '../services/imageUploadService';

interface ImageUploadComponentProps {
  onImageUploaded: (imageUrl: string) => void;
  currentImageUrl?: string;
  label?: string;
  className?: string;
}

const ImageUploadComponent: React.FC<ImageUploadComponentProps> = ({
  onImageUploaded,
  currentImageUrl,
  label = "Wybierz zdjęcie",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    setError(null);
    
    // Walidacja pliku
    const validation = imageUploadService.validateFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Nieprawidłowy plik');
      return;
    }

    setUploading(true);

    try {
      const result = await imageUploadService.uploadImage(file);
      onImageUploaded(result.data.display_url);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Błąd przesyłania obrazu');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${dragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={uploading}
        />

        {currentImageUrl ? (
          <div className="space-y-3">
            <img
              src={currentImageUrl}
              alt="Podgląd"
              className="mx-auto max-h-32 rounded-lg object-cover"
            />
            <p className="text-sm text-gray-600">
              Kliknij lub przeciągnij nowe zdjęcie aby zmienić
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="mx-auto w-12 h-12 text-gray-400">
              📸
            </div>
            <div>
              <p className="text-gray-600">
                Kliknij aby wybrać zdjęcie lub przeciągnij tutaj
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, WebP do 32MB
              </p>
            </div>
          </div>
        )}

        {uploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg"
          >
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-sm text-gray-600">Przesyłanie...</span>
            </div>
          </motion.div>
        )}
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm"
        >
          {error}
        </motion.div>
      )}
    </div>
  );
};

export default ImageUploadComponent;
